import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Dialog, DialogActions, DialogContent, MenuItem, Stack } from '@mui/material';
import { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
    ControlledAutocomplete,
    ControlledInput,
    ControlledSelect,
    DialogTitleWithClose,
    IResidenceForm,
    IUpdateResidence,
    Section,
} from '../../../shared';
import { FamilySituation, LivingSituation } from '../../enums';
import { useUpdateAddress } from '../../hooks';
import { residenceFromFormMapper, residenceToFormMapper } from '../../mappers';
import { useResidenceSchema } from '../../validators';
import { validateOriginOption } from '../../../shared/utils/origins.util';
import { getCountries } from '../../../shared/constants/countries.constant';

interface Props {
    open: boolean;
    supportCaseId: string;
    address: IUpdateResidence;
    handleClose: () => void;
}

export const UpdateResidenceModal: FC<Props> = ({ address, handleClose, open, supportCaseId }) => {
    const { t } = useTranslation();

    const { mutateAsync: updateAddress } = useUpdateAddress(supportCaseId);

    const form = useForm<IResidenceForm>({
        resolver: yupResolver(useResidenceSchema()),
        mode: 'all',
    });

    const onSubmit = async (data: IResidenceForm) => {
        await updateAddress(residenceFromFormMapper(data));
        handleClose();
    };

    useEffect(() => {
        if (address) {
            form.reset(residenceToFormMapper(address));
        }
    }, [address, form, open]);

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <DialogTitleWithClose onClose={handleClose}>{t('editResidence')}</DialogTitleWithClose>
                    <DialogContent>
                        <Stack spacing={2} width="100%">
                            <Section title={t('familySituation')}>
                                <ControlledSelect name={`familySituation`} label={t('familySituation')}>
                                    <MenuItem value={''}>{t('unknown')}</MenuItem>
                                    {Object.values(FamilySituation).map((situation) => (
                                        <MenuItem value={situation} key={situation}>
                                            {t(situation)}
                                        </MenuItem>
                                    ))}
                                </ControlledSelect>
                            </Section>

                            <Section title={t('livingSituation')}>
                                <ControlledSelect name={`livingSituation`} label={t('livingSituation')}>
                                    <MenuItem value={''}>{t('unknown')}</MenuItem>
                                    {Object.values(LivingSituation).map((situation) => (
                                        <MenuItem value={situation} key={situation}>
                                            {t(situation)}
                                        </MenuItem>
                                    ))}
                                </ControlledSelect>
                            </Section>

                            <Section title={t('addressLabel')}>
                                <ControlledInput name="street" label={t('streetLabel')} />
                                <ControlledInput name="number" label={t('numberLabel')} />
                                <ControlledInput name="box" label={t('boxLabel')} />
                                <ControlledInput name="postalCode" label={t('postalCodeLabel')} />
                                <ControlledInput name="city" label={t('cityLabel')} />
                                <ControlledAutocomplete
                                    name="country"
                                    label={t('countryLabel')}
                                    defaultValue={null}
                                    options={getCountries}
                                    getOptionLabel={(option) => option.label}
                                    isOptionEqualToValue={(option, value) => validateOriginOption(option, value)}
                                />
                            </Section>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button type="submit" variant="contained" color="primary">
                            {t('save')}
                        </Button>
                        <Button onClick={handleClose} color="secondary">
                            {t('cancel')}
                        </Button>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};
